import { useEffect, useRef, useState } from 'react';
import type { Agent } from '../../agents/registry.ts';
import { buildUploadNote, type UploadedFile } from '../../agents/uploadNote.ts';
import {
  streamAgentTurn,
  newTurnId,
  SessionExpiredError,
  DailyLimitError,
  StreamStalledError,
  type AgentCitation,
  type AgentStreamEvent,
} from '../../agents/agentStream.ts';
import { getOrCreateSessionId, touchSession } from '../../agents/sessionStore.ts';
import type { SessionTranscriptTurn, TranscriptToolCall } from '../../agents/sessionApi.ts';
import type { SessionLogApi } from './useSessionLog.ts';
import { recordStreamOutcome, recordStreamStalled } from './streamTelemetry.ts';

/** A message (user or agent) in the chat transcript. */
export interface ChatMsg {
  kind: 'msg';
  id: number;
  role: 'user' | 'agent';
  text: string;
  /** True while tokens are still streaming into this message. */
  streaming: boolean;
  /** Deterministic source citations for an agent turn, set on `complete`.
   *  Rendered as a clickable "Sources" strip independent of the reply text. */
  citations?: readonly AgentCitation[];
  /** Human feedback the user gave on this agent turn via the thumbs buttons.
   *  Set optimistically on click and locked in (undefined = not yet rated).
   *  Reverted to undefined if the backend POST fails so the user can retry. */
  feedback?: 'up' | 'down';
}

/** An inline tool-call card rendered between messages. Fields come from the
 *  agent's stream, so values are rendered as escaped text (never raw HTML). */
export interface ChatTool {
  kind: 'tool';
  id: number;
  name: string;
  args: Record<string, unknown>;
  /** Result outcome once the call returns; null while still invoking. */
  ok: boolean | null;
  /** One-line result summary once returned; null while still invoking. */
  summary: string | null;
  returned: boolean;
}

/** A terminal error notice surfaced in the transcript. */
export interface ChatError {
  kind: 'error';
  id: number;
  message: string;
  /** When set, the failure is retryable — the original user input, so the UI
   *  can offer a "Retry" button that re-sends it without a duplicate bubble.
   *  Absent for non-retryable failures (e.g. an expired session). */
  retryText?: string;
  /** The failed turn's id, carried so the retry re-sends it VERBATIM. The
   *  runtime refuses a second claim of the same id, so if the original request
   *  did reach the server after all, the retry can't run the turn twice — it
   *  reconciles from the transcript instead. Riding on the notice (not a ref)
   *  keeps it correct when another turn was sent in between. */
  retryTurnId?: string;
}

/** A shared document surfaced as a rich card (countdown + open), synthesized
 *  from a `doc_share_url` tool result — replaces the raw presigned-URL link. */
export interface ChatDoc {
  kind: 'doc';
  id: number;
  /** Filename for display, e.g. "lobby-incident.md". */
  name: string;
  /** Backend document path — used to ask the agent for a fresh link on expiry. */
  path: string;
  /** Presigned download URL. */
  url: string;
  /** ISO expiry timestamp driving the countdown; '' when unknown. */
  expiresAt: string;
}

/** A daily-usage-cap notice with a live countdown to reset. Distinct from
 *  {@link ChatError} so the transcript can render the countdown + recharge bar
 *  (see DailyLimitNotice) rather than a flat error line. */
export interface ChatLimit {
  kind: 'limit';
  id: number;
  /** The daily prompt cap that was hit. */
  limit: number;
  /** ISO-8601 instant the cap resets (the caller's next local midnight). */
  resetAt: string;
}

/** A destructive-action confirmation surfaced from a `doc_delete` tool call
 *  that returned `confirmation_required`. Rendered as a Yes/No card; "Yes"
 *  re-asks the agent to delete with the user's approval, "No" dismisses it. */
export interface ChatConfirm {
  kind: 'confirm';
  id: number;
  /** The document path the agent proposes to delete. */
  path: string;
  /** Human-readable confirmation question to show with the Yes/No buttons. */
  prompt: string;
}

/** A transient placeholder shown on entry while the console resolves session
 *  state (loading history, possibly restoring a transcript). It's replaced
 *  exactly once it resolves — by the restored transcript or the agent's
 *  greeting — so the greeting never renders prematurely and then visibly swap
 *  to a "welcome back" variant. */
export interface ChatPreparing {
  kind: 'preparing';
  id: number;
  /** What this placeholder is covering: restoring a prior session (entry/reload)
   *  vs. spinning up a freshly-created one (the "New session" action). Drives the
   *  notice copy. Defaults to restoring. */
  mode?: 'restoring' | 'creating';
}

/** An in-chat image-generation widget (C8), created when `generate_image`
 *  settles with a queued job — or re-created from a restored transcript so a
 *  prior conversation shows its images inline. The card itself owns the slow
 *  status polling and the completed thumbnail/download UI (see ImageJobCard). */
export interface ChatImageJob {
  kind: 'imageJob';
  id: number;
  /** Studio job id. Known for live cards and for restored turns carrying the
   *  structured `imageJobs` transcript field; absent for legacy restored turns,
   *  where the card resolves the job by record-time proximity to `turnTs`. */
  jobId?: string;
  /** The prompt sent to the generator (card title). */
  prompt: string;
  /** The assistant turn's timestamp (restored cards) — anchors the legacy
   *  nearest-record match when `jobId` is unknown. */
  turnTs?: string;
}

/** A console file upload surfaced in the transcript: progress while it
 *  transfers, then the in-agent path and its expiry — or the failure. */
export interface ChatUpload {
  kind: 'upload';
  id: number;
  name: string;
  /** Relative document path; '' until the upload completes. */
  path: string;
  /** 0..1. */
  progress: number;
  status: 'uploading' | 'done' | 'error';
  /** Failure reason; set only when `status` is 'error'. */
  message?: string;
}

export type ChatItem =
  | ChatMsg
  | ChatTool
  | ChatError
  | ChatDoc
  | ChatLimit
  | ChatConfirm
  | ChatPreparing
  | ChatImageJob
  | ChatUpload;


/** Derive an ISO expiry from a presigned URL's `X-Amz-Date` + `X-Amz-Expires`,
 *  as a fallback when the tool result omits a structured `expiresAt`. */
function deriveExpiryFromUrl(url: string): string | null {
  try {
    const q = new URL(url).searchParams;
    const date = q.get('X-Amz-Date');
    const secs = Number(q.get('X-Amz-Expires'));
    const m = date && /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date);
    if (!m || !Number.isFinite(secs)) {
      return null;
    }
    const start = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    return new Date(start + secs * 1000).toISOString();
  } catch {
    return null;
  }
}

/** Parse a `doc_share_url` tool result into a document-card payload, or null if
 *  it doesn't carry a URL. Prefers the structured `expiresAt`. */
export function parseShareDoc(
  output: string,
): { name: string; path: string; url: string; expiresAt: string } | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(output);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const rec = parsed as Record<string, unknown>;
  if (typeof rec.url !== 'string') {
    return null;
  }
  const path = typeof rec.path === 'string' ? rec.path : '';
  const name = path.split('/').pop() || 'document';
  const expiresAt =
    typeof rec.expiresAt === 'string' ? rec.expiresAt : (deriveExpiryFromUrl(rec.url) ?? '');
  return { name, path, url: rec.url, expiresAt };
}

/** Parse a `doc_delete` tool result into a confirmation prompt, or null if it
 *  isn't a `confirmation_required` payload (i.e. it was an actual delete). The
 *  delete tool is gated server-side: an unconfirmed call returns
 *  `{ status: 'confirmation_required', path, prompt }` and deletes nothing. */
export function parseDeleteConfirm(output: string): { path: string; prompt: string } | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(output);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const rec = parsed as Record<string, unknown>;
  if (rec.status !== 'confirmation_required') {
    return null;
  }
  const path = typeof rec.path === 'string' ? rec.path : '';
  const prompt =
    typeof rec.prompt === 'string'
      ? rec.prompt
      : `Delete ${path || 'this file'}? This can't be undone.`;
  return { path, prompt };
}

/** Whether a settled document-tool call actually changed the user's files, so
 *  the Files panel should refresh. A successful write/append or a confirmed
 *  delete (`deleted:true`) counts; an error or a `confirmation_required` does
 *  not (the latter deletes nothing). */
/** A settled `generate_image` result that actually queued a job (not an error). */
function isImageJobEnqueued(output: string): boolean {
  try {
    const parsed = JSON.parse(output) as Record<string, unknown>;
    return parsed.ok === true && typeof parsed.jobId === 'string';
  } catch {
    return false;
  }
}

function isDocMutation(name: string, output: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(output);
  } catch {
    return false;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return false;
  }
  const rec = parsed as Record<string, unknown>;
  if ('reason' in rec) {
    return false; // an error result
  }
  if (name === 'doc_write' || name === 'doc_append') {
    return typeof rec.path === 'string';
  }
  if (name === 'doc_delete') {
    return rec.deleted === true;
  }
  return false;
}

export interface ToolLogEntry {
  id: number;
  name: string;
  /** One-line collapsed view of the call's arguments. */
  args: string;
  /** Full, pretty-printed request input; populated when the call returns. */
  request: string;
  /** Full result output; empty while the call is still running. */
  result: string;
  stamp: string;
  running: boolean;
}

export type ChatStatus = 'idle' | 'streaming' | 'error';

export interface AgentChat {
  items: ChatItem[];
  toolLog: ToolLogEntry[];
  status: ChatStatus;
  /** Live "what the agent is doing now" label from server `status` frames
   *  (Thinking… / Searching the web… / Verifying facts…), or null when idle or
   *  once answer text starts streaming. Drives the typing-bubble activity line. */
  activity: string | null;
  /** The session id this transcript belongs to — included in feedback payloads
   *  so a rating can be correlated with the stored session on the backend. */
  currentSessionId: string;
  sendMessage: (text: string) => void;
  /** Re-send a turn that failed (e.g. a network drop), clearing its error
   *  notice. The user's original message is already in the transcript, so this
   *  re-streams without adding a duplicate user bubble. */
  retryMessage: (text: string, errorId: number) => void;
  /** Set (or clear, with undefined) the thumbs feedback on an agent message.
   *  Network-free: the caller owns the POST and reverts on failure. */
  setFeedback: (id: number, rating: 'up' | 'down' | undefined) => void;
  /** Add an upload card to the transcript; returns its id. */
  beginUpload: (name: string) => number;
  /** Update one card's transfer progress (0..1). */
  setUploadProgress: (id: number, progress: number) => void;
  /** Mark an upload stored, and queue the agent note that announces it. */
  completeUpload: (id: number, file: UploadedFile) => void;
  /** Mark an upload failed. Fires no turn — there is nothing to announce. */
  failUpload: (id: number, message: string) => void;
}

/** A failed `fetch` (server unreachable, offline, DNS, connection reset, or a
 *  mid-stream drop) throws a `TypeError` rather than an HTTP error. Treat those
 *  — plus an explicit offline signal — as retryable network failures. */
function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true;
  }
  if (err instanceof TypeError) {
    return true;
  }
  if (err instanceof Error) {
    return /networkerror|failed to fetch|load failed|network ?request failed/i.test(err.message);
  }
  return false;
}

const MAX_SUMMARY = 200;

/** How long a stalled turn keeps reconciling against the server-side
 *  transcript before giving up. The runtime finishes a turn it accepted even
 *  when the live stream dies (API Gateway allows turns up to 15 min under
 *  STREAM mode) and persists the reply BEFORE emitting the terminal frames —
 *  so patient polling converges on the reply unless the turn itself failed. */
const STALL_RECOVERY_WINDOW_MS = 18 * 60_000;
/** Transcript poll cadence during stall recovery. */
const STALL_RECOVERY_POLL_MS = 8_000;

function argText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function formatArgsLine(args: Record<string, unknown>): string {
  return Object.entries(args)
    .map(([key, value]) => `${key}=${argText(value)}`)
    .join(' · ');
}

function summarize(output: unknown): string {
  const text = typeof output === 'string' ? output : JSON.stringify(output ?? '');
  return text.length > MAX_SUMMARY ? `${text.slice(0, MAX_SUMMARY)}…` : text;
}

function stamp(): string {
  return new Date().toISOString().slice(11, 19);
}

/**
 * The agent's opening message for a fresh session. Two shapes:
 *  - First run (no prior sessions): introduce what the agent is and does.
 *  - Returning (`returning === true`): welcome the user back and, when known,
 *    nod to their most recent thread so they can pick it up or start anew.
 * `returning === undefined` (session history still loading) reads as first run;
 * the hook re-renders the greeting once history resolves (see the effect below).
 */
function greeting(
  agent: Agent,
  firstName?: string,
  returning?: boolean,
  lastTitle?: string,
): string {
  const name = firstName?.trim();
  if (returning) {
    const back = name ? `Welcome back, ${name}.` : 'Welcome back.';
    const recap = lastTitle ? ` Last time we were on “${lastTitle}.”` : '';
    return `${back}${recap} Want to pick that up, or start something new?`;
  }
  const hi = name ? `Hi ${name} — ` : 'Hi — ';
  return (
    `${hi}I'm ${agent.name}. I can look things up for you and remember your ` +
    `preferences and interests, so I get more helpful the more we talk. ` +
    `How may I assist you, today?`
  );
}

/** The prompt inside a `generate_image(...)` tool summary, when it carries one.
 *  Gives a fallback card a real title instead of a generic label. */
function promptFromToolSummary(toolSummary: string): string | undefined {
  const match = /\bgenerate_image\(([^)]*)\)/.exec(toolSummary);
  const prompt = match?.[1]?.trim();
  return prompt !== undefined && prompt.length > 0 ? prompt : undefined;
}

/**
 * Restored image cards for one assistant turn.
 *
 * The structured `imageJobs` field is authoritative when present — it carries
 * the jobId, so the card resolves exactly. Otherwise, ANY turn whose tool trace
 * shows a `generate_image` call gets one jobId-less card, which ImageJobCard
 * resolves by record-time proximity.
 *
 * That fallback used to be gated to turns before 2026-07-22, on the assumption
 * that every later turn would carry the structured field. It didn't: reviewed
 * agents (Vera) persist their transcript through a server-side write that
 * omitted `imageJobs` entirely, so EVERY image she generated restored as
 * nothing at all. The runtime is fixed, but those turns are unrecoverable —
 * their jobId was never stored anywhere — so the fallback has to be
 * unconditional to bring them back.
 *
 * The tradeoff the cutoff was protecting against is real but small: a turn
 * whose `generate_image` FAILED now grows a card too, since a one-line tool
 * summary cannot distinguish success from failure. Such a card finds no
 * matching record and settles into ImageJobCard's quiet "no longer available"
 * state — a better failure than silently dropping every real image.
 */
function imageItemsForTurn(turn: SessionTranscriptTurn, nextId: () => number): ChatImageJob[] {
  if (turn.imageJobs !== undefined) {
    return turn.imageJobs.map((job) => ({
      kind: 'imageJob' as const,
      id: nextId(),
      jobId: job.jobId,
      prompt: job.prompt.length > 0 ? job.prompt : 'Generated image',
      turnTs: turn.ts,
    }));
  }
  if (turn.toolSummary !== undefined && /\bgenerate_image\b/.test(turn.toolSummary)) {
    return [
      {
        kind: 'imageJob' as const,
        id: nextId(),
        prompt: promptFromToolSummary(turn.toolSummary) ?? 'Generated image',
        turnTs: turn.ts,
      },
    ];
  }
  return [];
}

/** Map a restored transcript to chat items. Each stored turn is one message;
 *  the one-line `toolSummary` is omitted (the restored view shows messages, not
 *  reconstructed tool cards), except that image-generating turns re-create
 *  their in-chat image cards so prior conversations show images inline.
 *  `nextId` keeps ids monotonic with live turns. */
function transcriptToItems(
  turns: readonly SessionTranscriptTurn[],
  nextId: () => number,
): ChatItem[] {
  return turns.flatMap((turn) => [
    {
      kind: 'msg' as const,
      id: nextId(),
      role: turn.role === 'user' ? ('user' as const) : ('agent' as const),
      text: turn.text,
      streaming: false,
    },
    ...(turn.role === 'assistant' ? imageItemsForTurn(turn, nextId) : []),
  ]);
}

/** Optional session wiring from `useSessions` (owned by `AgentConsole`). */
export interface AgentChatOptions {
  /** Current session id; the chat keys its mount/reset on this. Falls back to
   *  the persisted per-agent id when omitted (e.g. in unit tests). */
  readonly sessionId?: string;
  /** Fires on each turn's terminal frame carrying the runtime's daily
   *  budget snapshot (guest console details meter, tiers T3/T8). */
  readonly onUsageLimit?: (u: {
    limit: number;
    used: number;
    remaining: number;
    resetAt: string;
    omUsedToday?: number;
    omBudget?: number;
  }) => void;
  /** Endpoint override for non-Cognito callers (the GUEST console, tiers
   *  T8): the guest proxy URL + the pre-minted guest bearer. Omitted → the
   *  signed-in default. */
  readonly endpoint?: { readonly invokeUrl: string; readonly jwt: string };
  /** Transcript to restore into the foreground instead of the greeting. */
  readonly transcript?: readonly SessionTranscriptTurn[] | null;
  /** Signed-in user's first name, used to personalize the greeting. */
  readonly firstName?: string;
  /** Whether the user has chatted with this agent before (any prior session
   *  with turns). `undefined` while session history is still loading. Drives
   *  the first-run-vs-returning greeting. */
  readonly returning?: boolean;
  /** Title of the user's most recent prior thread, surfaced in the returning
   *  greeting when present. */
  readonly lastTitle?: string;
  /** Bumped by `useSessions` each time it restores a transcript for the SAME
   *  session id (e.g. rehydrating the resumed session on reload). The reset
   *  effect keys on it so the restored transcript renders even though the
   *  session id didn't change — selecting a *different* session already re-keys
   *  via `sessionId`, but a reload resumes the same id, so that change alone is
   *  invisible to the reset effect. */
  readonly restoreKey?: number;
  /** Called when a settled document tool changes the user's files (a successful
   *  write/append or a confirmed delete), so the Files panel can refresh. */
  readonly onDocumentsChanged?: () => void;
  /** Called when a `generate_image` call settles with a queued job, so the
   *  Jobs panel's Images sub-list shows the new "Generating…" row without a
   *  page reload (the C7 record exists server-side from this instant). */
  readonly onImageJobCreated?: () => void;
  /** Called with each turn's streamed `memory-write` frame, so the inspector's
   *  "Memory › All" list can grow alongside the graph without re-polling S3
   *  (see `useAgentMemory.applyMemoryWrite`). */
  readonly onMemoryWrite?: (
    event: Extract<AgentStreamEvent, { type: 'memory-write' }>,
  ) => void;
  /** Called with each live `workflow-progress` frame while a `workflow_run`
   *  tool call executes, so the run card updates from the stream instead of
   *  polling (see `useWorkflowRunProgress`). Ephemeral UI — never written to
   *  the transcript or the session log. */
  readonly onWorkflowProgress?: (
    event: Extract<AgentStreamEvent, { type: 'workflow-progress' }>,
  ) => void;
  /** True while session history is still loading on entry. Drives the transient
   *  "preparing" placeholder so the greeting doesn't render prematurely and then
   *  swap to a "welcome back" variant once history resolves. */
  readonly loading?: boolean;
  /** True while a freshly-started session is being spun up (the "New session"
   *  action). Switches the "preparing" placeholder's copy from restoring → creating. */
  readonly creating?: boolean;
}

/**
 * Drives the console's chat against the agent's real streaming API. Owns the
 * transcript (messages + inline tool cards) and the tool-use log, all sourced
 * from the SSE events the agent emits (`src/agents/agentStream.ts`).
 *
 * It no longer mutates the memory graph directly: each turn's events are emitted
 * into the **session log** (`log`, from `useSessionLog`), and the graph is a pure
 * projection of that log (`logToGraph`, applied in `AgentConsole`). This keeps a
 * single source of truth for the conversation — see `plans/memory-unification.md`.
 *
 * One turn runs at a time; `sendMessage` is a no-op while a turn is streaming.
 * The session id comes from `options.sessionId` (owned by `useSessions`) so the
 * chat resets and the agent's server-side continuity follow the selected
 * session; switching sessions re-keys the chat and can restore a transcript.
 * The in-flight request is aborted on unmount or session switch.
 *
 * `expireSession` (when supplied) is invoked if a turn fails with a
 * `SessionExpiredError` (the endpoint 401'd): the auth provider then drops to
 * 'unauthenticated' and `<RequireAuth>` routes the user back to sign-in, rather
 * than leaving them stuck re-sending turns that all 401.
 */
/** Spread-ready endpoint override (module level keeps sendTurn under the
 *  complexity pin). */
function endpointOverrideOf(
  options: AgentChatOptions | undefined,
): { endpoint: { invokeUrl: string; jwt: string } } | Record<string, never> {
  return options?.endpoint !== undefined ? { endpoint: options.endpoint } : {};
}

export function useAgentChat(
  agent: Agent,
  log: SessionLogApi,
  expireSession?: () => void,
  options?: AgentChatOptions,
): AgentChat {
  const [items, setItems] = useState<ChatItem[]>([]);
  // Mirror of `items` for reads inside event callbacks — the Retry handler needs
  // the failed notice's turn id at click time, long after the closure that
  // created the notice has gone.
  const itemsRef = useRef<ChatItem[]>([]);
  itemsRef.current = items;
  const [toolLog, setToolLog] = useState<ToolLogEntry[]>([]);
  const [status, setStatus] = useState<ChatStatus>('idle');
  // Live "what the agent is doing now" label (from server `status` frames):
  // "Thinking…", "Searching the web…", "Verifying facts…". Shown while a turn
  // is in flight and no answer text has arrived yet; null clears the line.
  const [activity, setActivity] = useState<string | null>(null);

  const idRef = useRef(0);
  const mountedRef = useRef(true);
  // Function-reads: these refs are mutated by other closures (unmount,
  // session switches); a call returns honest un-narrowed values where
  // direct property reads get spuriously narrowed across awaits.
  const mountedNow = (): boolean => mountedRef.current;
  const sessionIdNow = (): string => sessionIdRef.current;
  const busyRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string>('');
  const agentMsgIdRef = useRef<number | null>(null);
  // In-flight tool calls by toolUseId → their transcript + log ids, so the
  // matching `tool-result` flips the right card and row.
  // `itemId` is absent for suppressed doc tools (no inline transcript card).
  const toolCallsRef = useRef<Map<string, { itemId?: number; logId: number }>>(new Map());
  // Every toolUseId the live stream rendered this turn. Tool frames stream
  // while a reviewed turn withholds only TEXT, so a dropped stream can leave
  // some cards on screen and some never delivered — recovery must add exactly
  // the missing ones, not duplicate the rest.
  const renderedToolsRef = useRef<Set<string>>(new Set());

  const nextId = (): number => ++idRef.current;
  const endpointOverride = endpointOverrideOf(options);

  // The session the chat is bound to. `useSessions` supplies it in-app; the
  // fallback keeps the hook usable standalone (unit tests) by resolving the
  // persisted per-agent id. The chat's reset effect keys on this value.
  const effectiveSessionId = options?.sessionId ?? getOrCreateSessionId(agent.slug);
  // Latest restored transcript, read inside the reset effect. Held in a ref so
  // a new array identity per render doesn't re-fire the effect; `useSessions`
  // updates sessionId + transcript together, so when the effect runs (sessionId
  // changed) the matching transcript is already here.
  const transcriptRef = useRef<readonly SessionTranscriptTurn[] | null>(
    options?.transcript ?? null,
  );
  transcriptRef.current = options?.transcript ?? null;
  // First name + returning-status + last-thread title are read (not deps)
  // inside the reset effect, so values that resolve slightly after mount don't
  // re-fire it and wipe the chat. A dedicated effect re-renders just the
  // greeting when the returning status lands (see below).
  const firstNameRef = useRef<string | undefined>(options?.firstName);
  firstNameRef.current = options?.firstName;
  const returningRef = useRef<boolean | undefined>(options?.returning);
  returningRef.current = options?.returning;
  const lastTitleRef = useRef<string | undefined>(options?.lastTitle);
  lastTitleRef.current = options?.lastTitle;
  // Read imperatively when a doc tool settles; held in a ref so a mid-stream
  // re-render with a new callback identity doesn't strand a stale closure.
  const onDocsChangedRef = useRef<(() => void) | undefined>(options?.onDocumentsChanged);
  onDocsChangedRef.current = options?.onDocumentsChanged;
  const onImageJobRef = useRef<(() => void) | undefined>(options?.onImageJobCreated);
  onImageJobRef.current = options?.onImageJobCreated;
  const onMemoryWriteRef = useRef<AgentChatOptions['onMemoryWrite']>(options?.onMemoryWrite);
  onMemoryWriteRef.current = options?.onMemoryWrite;
  const onUsageLimitRef = useRef<AgentChatOptions['onUsageLimit']>(options?.onUsageLimit);
  onUsageLimitRef.current = options?.onUsageLimit;
  const onWorkflowProgressRef = useRef<AgentChatOptions['onWorkflowProgress']>(
    options?.onWorkflowProgress,
  );
  onWorkflowProgressRef.current = options?.onWorkflowProgress;
  // Whether session history is still loading; read inside the reset effect so a
  // fresh mount shows the "preparing" placeholder instead of a premature
  // greeting. A dedicated effect swaps in the real greeting once it resolves.
  const loadingRef = useRef<boolean>(options?.loading ?? false);
  loadingRef.current = options?.loading ?? false;
  const creatingRef = useRef<boolean>(options?.creating ?? false);
  creatingRef.current = options?.creating ?? false;

  const appendToken = (text: string): void => {
    if (!text) {
      return;
    }
    if (agentMsgIdRef.current === null) {
      const id = nextId();
      agentMsgIdRef.current = id;
      setItems((prev) => [...prev, { kind: 'msg', id, role: 'agent', text, streaming: true }]);
      return;
    }
    const id = agentMsgIdRef.current;
    setItems((prev) =>
      prev.map((it) =>
        it.kind === 'msg' && it.id === id ? { ...it, text: it.text + text, streaming: true } : it,
      ),
    );
  };

  const finalizeAgentMsg = (): void => {
    const id = agentMsgIdRef.current;
    if (id === null) {
      return;
    }
    setItems((prev) =>
      prev.map((it) => (it.kind === 'msg' && it.id === id ? { ...it, streaming: false } : it)),
    );
    agentMsgIdRef.current = null;
  };

  // `tool-call-start` carries only id+name; the args + result arrive on
  // `tool-call-end`, so the card opens bare and fills in when it returns. The
  // graph's (aggregated) tool node is emitted into the session log on
  // `tool-call-end` — when name + args + output are all known — not here.
  const startTool = (toolUseId: string, name: string): void => {
    renderedToolsRef.current.add(toolUseId);
    const logId = nextId();
    // EVERY tool renders an inline card in the transcript — exposing the agent's
    // inner workings is a product value proposition, not noise to hide (see
    // AGENTS.md). Cards are collapsed by default, so detail is available without
    // clutter. The richer doc cards (DocumentCard / ConfirmCard) are shown IN
    // ADDITION, not instead.
    // Close any open message so the card sits between message segments.
    finalizeAgentMsg();
    const itemId = nextId();
    setItems((prev) => [
      ...prev,
      { kind: 'tool', id: itemId, name, args: {}, ok: null, summary: null, returned: false },
    ]);
    toolCallsRef.current.set(toolUseId, { itemId, logId });
    setToolLog((prev) => [
      { id: logId, name, args: '', request: '', result: '', stamp: stamp(), running: true },
      ...prev,
    ]);
  };

  const endTool = (
    toolUseId: string,
    name: string,
    input: Record<string, unknown>,
    output: string,
  ): void => {
    const summary = summarize(output);
    const rec = toolCallsRef.current.get(toolUseId);
    if (rec) {
      toolCallsRef.current.delete(toolUseId);
      if (rec.itemId !== undefined) {
        setItems((prev) =>
          prev.map((it) =>
            it.kind === 'tool' && it.id === rec.itemId
              ? { ...it, args: input, ok: true, summary, returned: true }
              : it,
          ),
        );
      }
      setToolLog((prev) =>
        prev.map((row) =>
          row.id === rec.logId
            ? {
                ...row,
                args: formatArgsLine(input),
                request: JSON.stringify(input, null, 2),
                result: output,
                running: false,
                stamp: stamp(),
              }
            : row,
        ),
      );
    }
    // Surface a shared document as a rich card (countdown + open button) instead
    // of leaving the raw presigned URL to be auto-linked in the reply text.
    if (name === 'doc_share_url') {
      const doc = parseShareDoc(output);
      if (doc) {
        finalizeAgentMsg();
        setItems((prev) => [...prev, { kind: 'doc', id: nextId(), ...doc }]);
      }
    }
    // A destructive delete is gated: an unconfirmed `doc_delete` returns
    // `confirmation_required`, which we surface as an in-chat Yes/No card rather
    // than an inline tool result.
    // An unconfirmed `doc_delete` returns `confirmation_required`; surface the
    // in-chat Yes/No card in addition to the tool card (the inline tool card
    // itself is created by startTool/endTool like every other tool).
    if (name === 'doc_delete') {
      const confirm = parseDeleteConfirm(output);
      if (confirm) {
        finalizeAgentMsg();
        setItems((prev) => [...prev, { kind: 'confirm', id: nextId(), ...confirm }]);
      }
    }
    // A queued image job means a fresh C7 record — surface the in-chat card
    // (which polls the record to a terminal state, then shows thumbnail +
    // download), and refresh the Jobs panel so its "Generating…" row appears
    // mid-conversation, not on the next reload.
    if (name === 'generate_image' && isImageJobEnqueued(output)) {
      const parsed = JSON.parse(output) as { jobId: string };
      const promptArg = typeof input['prompt'] === 'string' ? input['prompt'] : 'Generated image';
      finalizeAgentMsg();
      setItems((prev) => [
        ...prev,
        { kind: 'imageJob', id: nextId(), jobId: parsed.jobId, prompt: promptArg },
      ]);
      onImageJobRef.current?.();
    }
    // Keeping an image flips its record (kept / expiry cleared) — refresh the
    // panels. A successful keep carries the same {ok, jobId} shape as enqueue.
    if (name === 'image_keep' && isImageJobEnqueued(output)) {
      onImageJobRef.current?.();
    }
    // Refresh the Files panel when a doc tool actually changed the user's files.
    if (isDocMutation(name, output)) {
      onDocsChangedRef.current?.();
    }
    // Emit the settled tool call into the session log — the graph's aggregated
    // per-turn tool node is built from these (in `logToGraph`). Recalled records
    // arrive separately, as the runtime's structured `memory-recall` stream frame
    // (handled in `handleEvent`), with real ids — not parsed from tool output.
    log.appendToolCall(name, input, output);
  };

  // Emit a streamed memory mutation into the session log; the graph projects it
  // (a written record becomes an in-context node linked to the turn's prompt).
  const applyMemoryWrite = (event: Extract<AgentStreamEvent, { type: 'memory-write' }>): void => {
    log.applyMemoryWrite(event.moduleId, event.op, event.records, event.recordIds);
  };

  // Replace the open message's text in place (guardrail-blocked supersedes any
  // already-streamed text with the canned response).
  const setAgentMsgText = (text: string): void => {
    if (agentMsgIdRef.current === null) {
      const id = nextId();
      agentMsgIdRef.current = id;
      setItems((prev) => [...prev, { kind: 'msg', id, role: 'agent', text, streaming: true }]);
      return;
    }
    const id = agentMsgIdRef.current;
    setItems((prev) =>
      prev.map((it) => (it.kind === 'msg' && it.id === id ? { ...it, text, streaming: true } : it)),
    );
  };

  const handleEvent = (event: AgentStreamEvent): void => {
    switch (event.type) {
      case 'status':
        // Live activity label; superseded by the next status or cleared once
        // real answer text starts streaming.
        setActivity(event.label);
        break;
      case 'text-delta':
        // Answer text is arriving — drop the activity line.
        setActivity(null);
        appendToken(event.delta);
        break;
      case 'tool-call-start':
        startTool(event.toolUseId, event.name);
        break;
      case 'tool-call-end':
        endTool(event.toolUseId, event.name, event.input, event.output);
        break;
      case 'memory-write':
        applyMemoryWrite(event);
        // Mirror the write into the inspector's "All" list (session tier grows
        // per turn) without a fire-and-forget S3 refetch race.
        onMemoryWriteRef.current?.(event);
        break;
      case 'memory-recall':
        // Cross-tier records pulled into context this turn → in-context graph
        // nodes (with real ids). Read-only: doesn't touch the "All" list.
        log.appendMemoryRecall(event.moduleId, event.records ?? []);
        break;
      case 'workflow-progress':
        // Live run-card update — ephemeral UI, not conversation state.
        onWorkflowProgressRef.current?.(event);
        break;
      case 'guardrail-blocked':
        // `complete` follows with the same output; it then just finalizes the
        // open message rather than appending a duplicate.
        setAgentMsgText(event.cannedOutput);
        break;
      case 'complete': {
        if (event.final.usageLimit !== undefined) {
          onUsageLimitRef.current?.(event.final.usageLimit);
        }
        // Render the final output only if nothing was streamed this turn.
        if (agentMsgIdRef.current === null && event.final.output) {
          appendToken(event.final.output);
        }
        // Bind the turn's deterministic source citations to the agent
        // message so the Sources strip renders independent of the reply text.
        // Drop document-tool citations (e.g. `doc_share_url`): those are the
        // user's own files surfaced through the DocumentCard widget, and their
        // presigned-link "domain" is the S3 bucket — not a real web source.
        const citations = event.final.citations?.filter((c) => !c.toolName.startsWith('doc_'));
        const msgId = agentMsgIdRef.current;
        if (citations !== undefined && citations.length > 0 && msgId !== null) {
          setItems((prev) =>
            prev.map((it) => (it.kind === 'msg' && it.id === msgId ? { ...it, citations } : it)),
          );
        }
        finalizeAgentMsg();
        break;
      }
      case 'error': {
        finalizeAgentMsg();
        const message = event.message || 'The agent reported an error.';
        setItems((prev) => [...prev, { kind: 'error', id: nextId(), message }]);
        setStatus('error');
        break;
      }
    }
  };

  // A stalled stream is NOT a failed turn: the runtime keeps running and
  // persists the reply to the session transcript before it ever emits the
  // terminal frames. Poll that transcript until the reply lands (or the
  // window closes) and render it in place of the never-received stream tail.
  // Re-sending instead would run the whole turn — including any workflow it
  // started — a second time.
  /**
   * Rebuild the cards for tool calls the dead stream never delivered.
   *
   * Recovery restores the turn's TEXT from the transcript; before this, every
   * tool call still in flight when the connection died vanished silently — the
   * reply would cite a shared link with no card and no Tool Use row behind it,
   * and nothing anywhere said a thing was missing. Calls already on screen are
   * skipped by `toolUseId`, so a partial drop reconciles instead of doubling.
   *
   * Cards are spliced in FRONT of an open reply message: they explain the text,
   * and the live path (`startTool`) closes the message so the card sits between
   * segments. Appending would invert that.
   */
  const restoreMissingToolCalls = (tools: readonly TranscriptToolCall[]): void => {
    const missing = tools.filter((t) => !renderedToolsRef.current.has(t.toolUseId));
    if (missing.length === 0) {
      return;
    }
    for (const call of missing) {
      renderedToolsRef.current.add(call.toolUseId);
    }
    const cards: ChatItem[] = missing.map((call) => ({
      kind: 'tool' as const,
      id: nextId(),
      name: call.name,
      args: call.args,
      // Mirrors `endTool`: a returned call is `ok`. The tool's own success or
      // failure lives in `result`, which the card summarizes.
      ok: true,
      summary: summarize(call.result),
      returned: true,
    }));
    setItems((prev) => {
      const openMsg = agentMsgIdRef.current;
      const at =
        openMsg === null ? -1 : prev.findIndex((it) => it.kind === 'msg' && it.id === openMsg);
      return at === -1
        ? [...prev, ...cards]
        : [...prev.slice(0, at), ...cards, ...prev.slice(at)];
    });
    // The Tool Use panel is newest-first, so reverse to keep call order.
    const rows = [...missing].reverse().map((call) => ({
      id: nextId(),
      name: call.name,
      args: formatArgsLine(call.args),
      request: JSON.stringify(call.args, null, 2),
      result: call.result,
      stamp: stamp(),
      running: false,
    }));
    setToolLog((prev) => [...rows, ...prev]);
    // A recovered share link is only useful as a card — the 2KB result bound
    // exists precisely so the presigned URL survives truncation intact.
    for (const call of missing) {
      if (call.name !== 'doc_share_url') {
        continue;
      }
      const doc = parseShareDoc(call.result);
      if (doc) {
        setItems((prev) => [...prev, { kind: 'doc', id: nextId(), ...doc }]);
      }
    }
  };

  const recoverStalledTurn = async (
    text: string,
    sessionId: string,
    tally: { polls: number; fetchFailures: number },
  ): Promise<boolean> => {
    // Lazy import: the sessions client drags in the auth stack; load it only
    // when a stall actually needs transcript reconciliation.
    const { getSession } = await import('../../agents/sessionApi.ts');
    const deadline = Date.now() + STALL_RECOVERY_WINDOW_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, STALL_RECOVERY_POLL_MS));
      if (!mountedRef.current || sessionIdRef.current !== sessionId) {
        return false; // unmounted or switched away — stop quietly
      }
      let turns: readonly SessionTranscriptTurn[];
      tally.polls += 1;
      try {
        turns = (await getSession(agent.slug, sessionId)).turns;
      } catch {
        // Counted, not swallowed. Recovery legitimately retries a transient
        // failure — but an 18-minute outage of the sessions endpoint used to
        // produce a byte-identical result to "the runtime never wrote the
        // transcript", and those are different problems with different fixes.
        tally.fetchFailures += 1;
        continue; // keep polling until the deadline
      }
      if (!mountedNow() || sessionIdNow() !== sessionId) {
        return false;
      }
      // The reply is the first assistant turn after the LAST occurrence of our
      // user message (persisted turns are append-only and ordered).
      let lastUser = -1;
      for (let i = turns.length - 1; i >= 0; i--) {
        if (turns[i].role === 'user' && turns[i].text === text) {
          lastUser = i;
          break;
        }
      }
      if (lastUser === -1) {
        continue;
      }
      const reply = turns.slice(lastUser + 1).find((t) => t.role === 'assistant');
      if (reply === undefined) {
        continue;
      }
      // Tool cards first: they explain the reply, and the persisted turn is the
      // only remaining record of calls whose frames never arrived.
      restoreMissingToolCalls(reply.toolCalls ?? []);
      // Replace (don't append to) any partial text: the persisted turn is the
      // authoritative full reply.
      setAgentMsgText(reply.text);
      finalizeAgentMsg();
      return true;
    }
    return false;
  };

  // Stream one turn for `text`. The caller owns adding the user bubble — a
  // first send adds it; a retry reuses the one already in the transcript.
  const streamTurn = async (text: string, turnId: string): Promise<void> => {
    busyRef.current = true;
    setStatus('streaming');
    setActivity(null);
    // Real conversation activity — keep this session from tripping the idle
    // limit so an actively-used chat is never expired out from under the user.
    touchSession(agent.slug);

    // Record the user's turn in the session log; the graph projects it as a new
    // prompt node — chained to the prior prompt by a `session` edge and lit as
    // the in-context turn (see `logToGraph`).
    log.appendUserMessage(text);
    agentMsgIdRef.current = null;
    renderedToolsRef.current = new Set();

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamAgentTurn(
        {
          agentId: agent.slug,
          input: text,
          sessionId: sessionIdRef.current,
          turnId,
          signal: controller.signal,
          ...endpointOverride,
        },
        (event) => {
          if (mountedRef.current) {
            handleEvent(event);
          }
        },
      );
      if (mountedRef.current) {
        finalizeAgentMsg();
      }
    } catch (err) {
      if (controller.signal.aborted) {
        // Turn cancelled (navigation/unmount) — nothing to surface.
      } else if (mountedRef.current) {
        finalizeAgentMsg();
        // A dead session can't be retried — hand off to the auth provider so it
        // purges the session and routes back to sign-in (no Retry button).
        if (err instanceof SessionExpiredError) {
          setItems((prev) => [...prev, { kind: 'error', id: nextId(), message: err.message }]);
          setStatus('error');
          expireSession?.();
        } else if (err instanceof DailyLimitError) {
          // Over the daily cap — keep the session and render a dedicated notice
          // that counts down to reset (no Retry; retrying can't help until the
          // window resets). The countdown is computed client-side from resetAt.
          setItems((prev) => [
            ...prev,
            { kind: 'limit', id: nextId(), limit: err.limit, resetAt: err.resetAt },
          ]);
          // Budget consumers (the guest details meter) see the CAPPED state
          // immediately — used pegged to the limit, zero remaining.
          onUsageLimitRef.current?.({
            limit: err.limit,
            used: err.limit,
            remaining: 0,
            resetAt: err.resetAt,
          });
          setStatus('error');
        } else if (err instanceof StreamStalledError) {
          // The connection died but the turn is (very likely) still running
          // server-side — recover its reply from the persisted transcript.
          // Status stays 'streaming' while we reconcile, which is accurate:
          // the agent IS still working. No Retry on failure: re-sending would
          // run the turn twice.
          const sessionId = sessionIdRef.current;
          const streamCtx = { turnId, agentId: agent.slug };
          // Emitted BEFORE recovery, not after. Recovery can run for 18
          // minutes and this whole arm is behind `mountedRef.current`, so a
          // user who gives up and navigates away would otherwise record
          // nothing at all — and that is exactly the user whose turn was lost.
          // The verdict follows as its own event, joined on turnId.
          recordStreamStalled(err.stats, streamCtx);
          const tally = { polls: 0, fetchFailures: 0 };
          const startedAt = Date.now();
          const recovered = await recoverStalledTurn(text, sessionId, tally);
          recordStreamOutcome(err.stats, streamCtx, {
            recovered,
            recoveryMs: Date.now() - startedAt,
            recoveryPolls: tally.polls,
            recoveryFetchFailures: tally.fetchFailures,
          });
          if (!recovered && mountedNow() && sessionIdNow() === sessionId) {
            setItems((prev) => [
              ...prev,
              {
                kind: 'error',
                id: nextId(),
                message:
                  `Lost the live connection while ${agent.name} was working, and the ` +
                  `reply hasn't appeared yet. It may still land in this session — ` +
                  `try reloading the page in a moment.`,
              },
            ]);
            setStatus('error');
          }
        } else {
          // Network drops are recoverable — give them agent-voiced copy and a
          // Retry button (via `retryText`). Other errors surface their message
          // without a retry affordance.
          const network = isNetworkError(err);
          const message = network
            ? `Couldn't reach ${agent.name} over the network — your message didn't get through. Check your connection and try again.`
            : err instanceof Error
              ? err.message
              : 'Something went wrong talking to the agent.';
          setItems((prev) => [
            ...prev,
            {
              kind: 'error',
              id: nextId(),
              message,
              ...(network ? { retryText: text, retryTurnId: turnId } : {}),
            },
          ]);
          setStatus('error');
        }
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      busyRef.current = false;
      if (mountedRef.current) {
        setStatus((prev) => (prev === 'error' ? 'error' : 'idle'));
        // An upload that completed mid-turn queued its note; now that the agent
        // is free, announce it.
        flushUploads();
      }
    }
  };

  const runTurn = async (text: string): Promise<void> => {
    setItems((prev) => [
      ...prev,
      { kind: 'msg', id: nextId(), role: 'user', text, streaming: false },
    ]);
    await streamTurn(text, newTurnId());
  };

  // ---- Console file uploads ------------------------------------------------
  //
  // Uploads whose note has not been fired yet, with the session they belong to.
  // A session switch aborts the running turn and rebuilds the transcript, so a
  // note that outlived its conversation must be DROPPED rather than announced
  // into a conversation that never saw the file.
  const pendingUploadsRef = useRef<{ sessionId: string; files: UploadedFile[] }>({
    sessionId: '',
    files: [],
  });

  const patchUpload = (id: number, patch: Partial<ChatUpload>): void => {
    setItems((prev) =>
      prev.map((it) => (it.kind === 'upload' && it.id === id ? { ...it, ...patch } : it)),
    );
  };

  const beginUpload = (name: string): number => {
    const id = nextId();
    setItems((prev) => [
      ...prev,
      { kind: 'upload', id, name, path: '', progress: 0, status: 'uploading' },
    ]);
    return id;
  };

  const setUploadProgress = (id: number, progress: number): void => {
    patchUpload(id, { progress });
  };

  const failUpload = (id: number, message: string): void => {
    patchUpload(id, { status: 'error', message });
  };

  /**
   * Fire the queued upload note as ONE turn, if the agent is free and the
   * session still matches. Called on completion and again whenever a turn
   * finishes, so a note that arrived mid-turn is not stranded.
   */
  const flushUploads = (): void => {
    const pending = pendingUploadsRef.current;
    if (pending.files.length === 0) {
      return;
    }
    if (pending.sessionId !== sessionIdRef.current) {
      // The conversation moved on; the file is still in the Files panel, only
      // the announcement is dropped.
      pendingUploadsRef.current = { sessionId: '', files: [] };
      return;
    }
    if (busyRef.current) {
      return; // a user-initiated send always wins; retried when it finishes
    }
    const note = buildUploadNote(pending.files);
    pendingUploadsRef.current = { sessionId: '', files: [] };
    if (note.length === 0) {
      return;
    }
    // No user bubble: the upload cards are the visible record, and the
    // transcript must never show a message the user did not write.
    void streamTurn(note, newTurnId());
  };

  const completeUpload = (id: number, file: UploadedFile): void => {
    patchUpload(id, { status: 'done', path: file.path, progress: 1 });
    const pending = pendingUploadsRef.current;
    // A completion from a previous session starts a fresh queue rather than
    // joining one that belongs to a conversation the user has left.
    pendingUploadsRef.current =
      pending.sessionId === sessionIdRef.current
        ? { sessionId: pending.sessionId, files: [...pending.files, file] }
        : { sessionId: sessionIdRef.current, files: [file] };
    flushUploads();
  };

  // Retry a failed turn: drop its error notice, then re-stream the same input.
  // The user's original message bubble is still in the transcript, so we don't
  // add another one. The ORIGINAL turn id goes back with it, so if the first
  // attempt actually reached the server (a failure before the response headers
  // looks identical to one that never arrived), the runtime refuses the second
  // claim and the turn runs once.
  const retryMessage = (text: string, errorId: number): void => {
    if (busyRef.current) {
      return;
    }
    const notice = itemsRef.current.find(
      (it): it is ChatError => it.kind === 'error' && it.id === errorId,
    );
    setItems((prev) => prev.filter((it) => !(it.kind === 'error' && it.id === errorId)));
    void streamTurn(text, notice?.retryTurnId ?? newTurnId());
  };

  const sendMessage = (raw: string): void => {
    const text = raw.trim();
    if (!text || busyRef.current) {
      return;
    }
    void runTurn(text);
  };

  // Stamp (or clear) the thumbs feedback on one agent message. Stays
  // network-free — AgentConsole owns the POST and calls this again with
  // `undefined` to revert the optimistic selection if the POST fails.
  const setFeedback = (id: number, rating: 'up' | 'down' | undefined): void => {
    setItems((prev) =>
      prev.map((it) =>
        it.kind === 'msg' && it.id === id ? { ...it, feedback: rating } : it,
      ),
    );
  };

  // The memory graph is no longer reset here — it is a pure projection of the
  // session log (`useSessionLog` rehydrates the log on session switch, and
  // `logToGraph` reconciles the graph), so clearing/rebuilding it happens there.

  // Reset the chat transcript on mount, agent change, AND session switch. The
  // restored transcript (when present) renders in place of the greeting; abort
  // any in-flight turn on unmount or session switch. StrictMode-safe.
  useEffect(() => {
    mountedRef.current = true;
    idRef.current = 0;
    sessionIdRef.current = effectiveSessionId;
    agentMsgIdRef.current = null;
    toolCallsRef.current = new Map();
    busyRef.current = false;

    const restored = transcriptRef.current;
    setItems(
      restored && restored.length > 0
        ? transcriptToItems(restored, nextId)
        : loadingRef.current
          ? // Session state still resolving — show a placeholder (an animated
            // progress bar) rather than a greeting that would swap a moment later.
            [{ kind: 'preparing', id: nextId(), mode: creatingRef.current ? 'creating' : 'restoring' }]
          : [
              {
                kind: 'msg',
                id: nextId(),
                role: 'agent',
                text: greeting(
                  agent,
                  firstNameRef.current,
                  returningRef.current,
                  lastTitleRef.current,
                ),
                streaming: false,
              },
            ],
    );
    setToolLog([]);
    setStatus('idle');
    setActivity(null);

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
    };
    // `transcriptRef` is read (not a dep) — it updates with `effectiveSessionId`
    // OR, when the resumed session is rehydrated on reload (same id), with
    // `restoreKey`, which re-runs this effect so the late-arriving transcript
    // renders.
  }, [agent, effectiveSessionId, options?.restoreKey]);

  // Once session history resolves, render the real greeting in place of the
  // "preparing" placeholder (or correct a pristine greeting to the returning
  // variant). Gated on `loading` so the swap happens exactly once, when state is
  // settled — never the premature greeting → "welcome back" flicker. Only ever
  // replaces the placeholder/pristine greeting, so a restored transcript or a
  // real conversation is left untouched.
  useEffect(() => {
    const restored = transcriptRef.current;
    if (restored && restored.length > 0) {
      return;
    }
    if (options?.loading) {
      return; // still preparing — keep the placeholder until state settles
    }
    const text = greeting(agent, firstNameRef.current, options?.returning, options?.lastTitle);
    setItems((prev) => {
      if (prev.length !== 1) {
        return prev;
      }
      const only = prev[0];
      const replaceable =
        only.kind === 'preparing' ||
        (only.kind === 'msg' && only.role === 'agent' && !only.streaming);
      if (!replaceable) {
        return prev;
      }
      if (only.kind === 'msg' && only.text === text) {
        return prev;
      }
      return [{ kind: 'msg', id: only.id, role: 'agent', text, streaming: false }];
    });
  }, [agent, options?.returning, options?.lastTitle, options?.loading]);

  return {
    items,
    toolLog,
    status,
    activity,
    currentSessionId: effectiveSessionId,
    sendMessage,
    retryMessage,
    setFeedback,
    beginUpload,
    setUploadProgress,
    completeUpload,
    failUpload,
  };
}
